// The caret in a cell, as the engine counts it: which piece (a paragraph the cell touches, or a
// line of its edited text) and a UTF-16 offset into that piece's text. A cell draws each piece
// as an element with data-piece, and the paragraph dividers between them hold no text.

export interface CaretAt {
  piece: number
  offset: number
}

/// The caret inside `cell`, or null if the selection is elsewhere or spans text.
export function caretIn(cell: Element): CaretAt | null {
  const selection = getSelection()
  if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) return null
  const node = selection.focusNode
  if (!node || !cell.contains(node)) return null
  const element = node instanceof Element ? node : node.parentElement
  const piece = element?.closest('[data-piece]')
  if (piece && cell.contains(piece)) {
    const range = document.createRange()
    range.selectNodeContents(piece)
    range.setEnd(node, selection.focusOffset)
    return { piece: Number((piece as HTMLElement).dataset.piece), offset: range.toString().length }
  }
  // Between pieces: on a divider, or at the cell's own edge. The caret goes to the end of the
  // piece before it, which is the same place in the document as the start of the next.
  const pieces = [...cell.querySelectorAll<HTMLElement>('[data-piece]')]
  const before = pieces.filter(p => {
    const range = document.createRange()
    range.setStart(node, selection.focusOffset)
    return range.comparePoint(p, 0) < 0
  })
  const last = before.at(-1)
  if (!last) return pieces.length ? { piece: 0, offset: 0 } : null
  return { piece: Number(last.dataset.piece), offset: last.textContent?.length ?? 0 }
}

/// Puts the caret at the start of a cell's first piece.
export function placeCaret(cell: HTMLElement) {
  const first = cell.querySelector<HTMLElement>('[data-piece]')
  if (!first) return
  cell.focus({ preventScroll: true })
  const selection = getSelection()
  if (!selection) return
  const range = document.createRange()
  range.setStart(first.firstChild ?? first, 0)
  range.collapse(true)
  selection.removeAllRanges()
  selection.addRange(range)
}
